'use client';

import React, { useState } from 'react';
import { X, CheckSquare } from 'lucide-react';
import { FarmTask, TaskCategory, TaskPriority, CATEGORY_CONFIG } from './tasksData';

interface TaskFormProps {
  open: boolean;
  editTask: FarmTask | null;
  onClose: () => void;
  onSave: (task: FarmTask) => void;
}

const STAFF = [
  'Ogenga Paul', 'Akello Grace', 'Okello James', 'Apio Christine',
  'Dr. Nakato Sarah', 'Mugisha Robert',
];

const FARMS = ['Idudi Poultry Farm', 'Kampala Demo Farm'];
const HOUSES = ['House A1', 'House A2', 'House B1', 'House B2', 'House C1', 'Layer House 01', 'Layer House 02', 'Layer House 03'];
const FLOCKS = ['BR-2026-001', 'BR-2026-002', 'LY-2026-001', 'LY-2026-002', 'LY-2026-003', 'KA-2026-001'];

export default function TaskForm({ open, editTask, onClose, onSave }: TaskFormProps) {
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState<Partial<FarmTask>>(editTask ?? {
    title: '',
    description: '',
    category: 'feeding',
    priority: 'medium',
    assignedTo: '',
    assignedToInitials: '',
    farm: 'Idudi Poultry Farm',
    house: '',
    flock: '',
    scheduledTime: '08:00',
    date: today,
    status: 'pending',
    createdBy: 'Okello James',
  });

  function handleChange(field: keyof FarmTask, value: string) {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'assignedTo') {
        const parts = value.split(' ');
        updated.assignedToInitials = parts.map((p) => p[0]).join('').toUpperCase().slice(0, 2);
      }
      return updated;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.assignedTo || !form.scheduledTime) return;
    const task: FarmTask = {
      id: editTask?.id ?? `task-${Date.now()}`,
      title: form.title!,
      description: form.description ?? '',
      category: (form.category as TaskCategory) ?? 'other',
      status: editTask?.status ?? 'pending',
      priority: (form.priority as TaskPriority) ?? 'medium',
      assignedTo: form.assignedTo!,
      assignedToInitials: form.assignedToInitials ?? form.assignedTo!.slice(0, 2).toUpperCase(),
      farm: form.farm ?? 'Idudi Poultry Farm',
      house: form.house || undefined,
      flock: form.flock || undefined,
      scheduledTime: form.scheduledTime!,
      date: form.date ?? today,
      createdBy: form.createdBy ?? 'Okello James',
      notes: form.notes,
    };
    onSave(task);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-card rounded-xl border shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ borderColor: 'var(--border)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <CheckSquare size={18} style={{ color: 'var(--primary)' }} />
            <h2 className="text-base font-semibold text-foreground">{editTask ? 'Edit Task' : 'New Task'}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          {/* Title */}
          <div>
            <label className="form-label">Task Title <span className="text-danger">*</span></label>
            <input
              type="text"
              value={form.title ?? ''}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g. Morning feeding — House A1"
              className="form-input w-full"
              required
            />
          </div>

          {/* Category + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Category</label>
              <select value={form.category ?? 'feeding'} onChange={(e) => handleChange('category', e.target.value)} className="form-input w-full">
                {(Object.keys(CATEGORY_CONFIG) as TaskCategory[]).map((cat) => (
                  <option key={cat} value={cat}>{CATEGORY_CONFIG[cat].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Priority</label>
              <select value={form.priority ?? 'medium'} onChange={(e) => handleChange('priority', e.target.value)} className="form-input w-full">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="form-label">Description</label>
            <textarea
              value={form.description ?? ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Task instructions or details..."
              className="form-input w-full resize-none"
              rows={2}
            />
          </div>

          {/* Assigned To + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Assigned To <span className="text-danger">*</span></label>
              <select value={form.assignedTo ?? ''} onChange={(e) => handleChange('assignedTo', e.target.value)} className="form-input w-full" required>
                <option value="">Select staff...</option>
                {STAFF.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Scheduled Time <span className="text-danger">*</span></label>
              <input
                type="time"
                value={form.scheduledTime ?? '08:00'}
                onChange={(e) => handleChange('scheduledTime', e.target.value)}
                className="form-input w-full"
                required
              />
            </div>
          </div>

          {/* Farm + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Farm</label>
              <select value={form.farm ?? ''} onChange={(e) => handleChange('farm', e.target.value)} className="form-input w-full">
                {FARMS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Date</label>
              <input
                type="date"
                value={form.date ?? today}
                onChange={(e) => handleChange('date', e.target.value)}
                className="form-input w-full"
              />
            </div>
          </div>

          {/* House + Flock */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">House (optional)</label>
              <select value={form.house ?? ''} onChange={(e) => handleChange('house', e.target.value)} className="form-input w-full">
                <option value="">None</option>
                {HOUSES.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Flock (optional)</label>
              <select value={form.flock ?? ''} onChange={(e) => handleChange('flock', e.target.value)} className="form-input w-full">
                <option value="">None</option>
                {FLOCKS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button type="submit" className="btn-primary flex-1">
              <CheckSquare size={14} />
              {editTask ? 'Update Task' : 'Create Task'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary px-4">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
